/**
 * Kit Service
 * Service for public Kit de Atendimento access (no authentication required)
 *
 * @version 2025.1.0
 */

import axios from 'axios';
import { KitValidationResult } from '../types/kit';

// API base URL - use /api/v1 for proxy, or full URL if specified
const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

/**
 * Kit Service
 * Handles public access to Kit de Atendimento
 */
class KitService {
  private baseUrl = `${API_BASE}/public`;

  /**
   * Get Kit de Atendimento by token
   * @param token - The kit access token
   * @returns Validation result with kit data or error
   */
  async getKitByToken(token: string): Promise<KitValidationResult> {
    try {
      const response = await axios.get(`${this.baseUrl}/kit/${token}`);
      return {
        valid: true,
        data: response.data.data,
      };
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const responseData = error.response?.data as { error?: string; message?: string } | undefined;

        if (status === 410) {
          return {
            valid: false,
            error: 'EXPIRED',
            message: responseData?.message || 'Este link expirou.',
          };
        }

        if (status === 404) {
          return {
            valid: false,
            error: 'NOT_FOUND',
            message: responseData?.message || 'Link inválido.',
          };
        }

        if (status === 429) {
          return {
            valid: false,
            error: 'RATE_LIMITED',
            message: responseData?.message || 'Muitas requisições. Tente novamente em 1 minuto.',
          };
        }
      }

      return {
        valid: false,
        error: 'UNKNOWN',
        message: 'Erro ao carregar dados do Kit.',
      };
    }
  }

  /**
   * Get document download URL
   * @param token - The kit access token
   * @param documentType - Type of document (guide, aso, record)
   * @returns Download URL
   */
  getDocumentDownloadUrl(token: string, documentType: 'guide' | 'aso' | 'record'): string {
    return `${this.baseUrl}/kit/${token}/document/${documentType}`;
  }

  /**
   * Get document view URL (inline PDF)
   * @param token - The kit access token
   * @param documentType - Type of document (guide, aso, record)
   * @returns View URL
   */
  getDocumentViewUrl(token: string, documentType: 'guide' | 'aso' | 'record'): string {
    return `${this.baseUrl}/kit/${token}/document/${documentType}/view`;
  }

  /**
   * Format appointment time for display
   * @param hour - Hour string
   * @param minute - Minute string
   * @returns Formatted time (HH:MM)
   */
  formatTime(hour: string | null, minute: string | null): string {
    if (!hour) return '--:--';
    const h = hour.padStart(2, '0');
    const m = (minute || '0').padStart(2, '0');
    return `${h}:${m}`;
  }

  /**
   * Format date for display in Brazilian format
   * @param dateString - Date string (YYYY-MM-DD or DD/MM/YYYY)
   * @returns Formatted date
   */
  formatDate(dateString: string): string {
    if (!dateString) return 'Data não disponível';

    // If already in Brazilian format
    if (dateString.includes('/')) {
      return dateString;
    }

    // Parse ISO format
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }

    return dateString;
  }
}

export const kitService = new KitService();
export default kitService;
